import { create } from "zustand";

import { authApi, normalizeUser, registrationToFormData, User } from "../services/auth.api";
import { ApiError } from "../services/api";

import { useAppStore } from "../store/appStore";

import {
  PendingRegistration,
  RegistrationValues,
} from "../components/register/types/registration";


type AuthStoreState = {
  /* ==================== State ==================== */

  pending: PendingRegistration | null;

  verified: boolean;

  isAuthenticated: boolean;

  currentUser: User | null;

  apiError: string;

  loginLoading: boolean;

  googleLoading: boolean;

  /* ==================== Actions ==================== */

  clearApiError: () => void;

  setCurrentUser: (user: User | null) => void;

  fetchCurrentUser: () => Promise<void>;

  register: (data: RegistrationValues) => Promise<void>;

  verifyCode: (code: string) => Promise<boolean>;

  resendCode: () => Promise<void>;

  login: (email: string, password: string) => Promise<boolean>;

  googleAuth: (accessToken: string) => Promise<void>;

  logout: () => void;
};

/* ===================== HELPERS ===================== */

function parseError(error: unknown): string {
  if (error instanceof ApiError) {
    return error.message;
  }

  return "Network error";
}

/* ===================== STORE ===================== */

export const useAuthStore = create<AuthStoreState>((set, get) => ({
  /* ==================== Initial State ==================== */

  pending: null,

  verified: false,

  isAuthenticated: false,

  currentUser: null,

  apiError: "",

  loginLoading: false,

  googleLoading: false,

  /* ==================== Utilities ==================== */

  clearApiError: () => {
    set({
      apiError: "",
    });
  },

  setCurrentUser: (user) => {
    set({
      currentUser: user,
      isAuthenticated: user !== null,
    });
  },

  /**
   * Fetch the current authenticated user from /auth/me.
   * Useful after page reload when we have a token but no user data.
   */
  fetchCurrentUser: async () => {
    try {
      const user = await authApi.me();

      set({
        currentUser: normalizeUser(user),
        isAuthenticated: true,
      });
    } catch (error) {
      // Token invalid or expired — clear state
      set({
        currentUser: null,
        isAuthenticated: false,
      });
    }
  },

  /* ==================== Register ==================== */

  register: async (data) => {
    set({
      apiError: "",
      verified: false,
      isAuthenticated: false,
      currentUser: null,
    });

    try {
      const formData = registrationToFormData(data);

      const res = await authApi.register(formData);

      set({
        pending: {
          email: data.email,
          code: res.verificationCode || "",
          payload: data,
        },
      });
    } catch (error) {
      set({
        apiError: parseError(error),
      });

      throw error;
    }
  },

  /* ==================== Verify Code ==================== */

  verifyCode: async (code) => {
    const pending = get().pending;

    if (!pending) {
      set({
        apiError: "Registration session has expired.",
      });

      return false;
    }

    const verificationCode = code.trim();

    if (!verificationCode) {
      set({
        apiError: "Please enter the verification code.",
      });

      return false;
    }

    set({
      apiError: "",
    });

    try {
      const res = await authApi.verifyEmail(
        pending.email,
        verificationCode
      );

      if (!res?.token) {
        set({
          apiError:
            "Verification succeeded but no authentication token was returned.",
        });

        return false;
      }

      set({
        verified: true,
        isAuthenticated: true,
        apiError: "",
        currentUser: normalizeUser(res.user),
      });

      return true;
    } catch (error) {
      const message = parseError(error);

      set({
        apiError: message,
        verified: false,
        isAuthenticated: false,
        currentUser: null,
      });

      return false;
    }
  },

  /* ==================== Resend Code ==================== */

  resendCode: async () => {
    const pending = get().pending;

    if (!pending) {
      set({
        apiError: "Registration session has expired.",
      });

      return;
    }

    set({
      apiError: "",
    });

    try {
      await authApi.resendVerification(pending.email);
    } catch (error) {
      set({
        apiError: parseError(error),
      });
    }
  },

  /* ==================== Login ==================== */

  login: async (email, password) => {
    set({
      loginLoading: true,
      apiError: "",
    });

    try {
      const res = await authApi.login(email, password);

      set({
        isAuthenticated: true,
        verified: true,
        apiError: "",
        currentUser: (res.user as User) ?? null,
      });

      return true;
    } catch (error) {
      set({
        isAuthenticated: false,
        verified: false,
        currentUser: null,
        apiError: parseError(error),
      });

      return false;
    } finally {
      set({
        loginLoading: false,
      });
    }
  },

  /* ==================== Google Auth ==================== */

  googleAuth: async (accessToken) => {
    set({
      googleLoading: true,
      apiError: "",
    });

    try {
      const res = await authApi.googleAuth(accessToken);

      useAppStore.getState().setAuthToken(res.token);

      set({
        verified: true,
        isAuthenticated: true,
        apiError: "",
      currentUser: normalizeUser(res.user),   // ✅
      });
    } catch (error) {
      set({
        apiError: parseError(error),
        isAuthenticated: false,
        currentUser: null,
      });

      throw error;
    } finally {
      set({
        googleLoading: false,
      });
    }
  },

  /* ==================== Logout ==================== */

  logout: () => {
    set({
      pending: null,
      verified: false,
      isAuthenticated: false,
      currentUser: null,
      apiError: "",
    });
  },

   me: async () => {
    return await authApi.me();
  },

}));
